/**
 * Sync Management Component
 *
 * Displays and manages all active folder syncs for a matter.
 * Features: manual sync trigger, remove/deactivate syncs
 */

import { formatDistanceToNow } from 'date-fns'
import { Link } from '@tanstack/react-router'
import {
  AlertTriangle,
  ArrowRight,
  Building2,
  Check,
  Clock,
  Database,
  Mail,
  RefreshCw,
  Trash2,
  UserRound
} from 'lucide-react'
import { Fragment, useEffect, useState, type ReactNode } from 'react'
import { toast } from 'react-hot-toast'
import { MicrosoftProviderIcon } from '@/components/shared/MicrosoftProviderIcon'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { FEATURE_FLAGS, useFeatures } from '@/hooks/useFeatures'
import { useOAuthPopupFlow } from '@/hooks/useOAuthPopupFlow'
import { backendClient } from '@/lib/api-client'
import { useMatterLabels } from '@/lib/useMatterLabels'
import {
  getMicrosoftFilesSourceLabel,
  isMicrosoftFilesProvider,
  MICROSOFT_FILES_OAUTH_PROVIDER_KEY
} from '@/lib/microsoftFiles'
import { CORRESPONDENCE_FOLDER_PATH } from '@/constants/document-constants'
import Spinner from '../shared/Spinner'
import { AddSyncDialog } from './AddSyncDialog'
import { SyncProviderCard } from './SyncProviderCard'
import { getAvailableSyncProviders } from './syncProviders'

interface FolderSyncFreshness {
  status: 'synced' | 'pending' | 'updating'
  at: string | null
}

interface SyncFolder {
  id: number
  provider_type: string
  provider_config: Record<string, any>
  sync_state: Record<string, any>
  is_active: boolean
  destination_folder_id: number
  destination_folder_guid: string | null
  destination_folder_name: string | null
  destination_folder_path: string | null
  destination_folder_display_path: string | null
  created_by_user_id: number
  created_by_user_name: string | null
  created_by_user_email: string | null
  last_synced_at: string | null
  freshness?: FolderSyncFreshness
  created_at: string
  group_id: string | null
}

interface SyncManagementProps {
  matterGuid: string
  className?: string
}

interface DeletionInfo {
  file_count: number
  folder_name: string
  provider_type: string
  sync_folder_ids?: number[]
  message: {
    delete_files: string
    keep_files: string
  }
}

function clusterSyncFolders(folders: SyncFolder[]): SyncFolder[][] {
  const seenGroups = new Set<string>()
  const clusters: SyncFolder[][] = []
  for (const folder of folders) {
    if (folder.group_id) {
      if (seenGroups.has(folder.group_id)) {
        continue
      }
      seenGroups.add(folder.group_id)
      clusters.push(folders.filter((item) => item.group_id === folder.group_id))
    } else {
      clusters.push([folder])
    }
  }
  return clusters
}

function folderFreshness(folder: SyncFolder): FolderSyncFreshness {
  return folder.freshness ?? {
    status: folder.last_synced_at ? 'synced' : 'pending',
    at: folder.last_synced_at,
  }
}

function groupedFreshness(
  members: SyncFolder[],
  isInactiveOrPaused: (folder: SyncFolder) => boolean,
): FolderSyncFreshness {
  const runningMembers = members.filter((folder) => !isInactiveOrPaused(folder))
  const considered = runningMembers.length > 0 ? runningMembers : members
  const statuses = considered.map(folderFreshness)
  if (statuses.some((item) => item.status === 'updating')) {
    return { status: 'updating', at: null }
  }
  if (statuses.every((item) => item.status === 'pending')) {
    return { status: 'pending', at: null }
  }
  let latestAt: string | null = null
  for (const item of statuses) {
    if (item.status === 'synced' && item.at && (!latestAt || item.at > latestAt)) {
      latestAt = item.at
    }
  }
  return { status: 'synced', at: latestAt }
}

function formatLastSync(dateString: string | null) {
  if (!dateString) return 'Pending first sync (may take a few minutes)'
  return formatDistanceToNow(new Date(dateString), { addSuffix: true })
}

function crimsonDestinationPath(folder: SyncFolder): string {
  return folder.destination_folder_display_path
    || folder.destination_folder_name
    || 'Root'
}

function isIManageContainerId(value: string): boolean {
  return /^[^/\s]+![^/\s]+$/.test(value.trim())
}

function imanageSourcePath(folder: SyncFolder): string {
  const storedPath = typeof folder.provider_config.folder_path === 'string'
    ? folder.provider_config.folder_path.trim()
    : ''
  if (storedPath && !isIManageContainerId(storedPath)) {
    return storedPath
  }
  if (isIManageWorkspaceSync(folder)) {
    return folder.provider_config.workspace_name || folder.provider_config.folder_name || 'Workspace'
  }
  const workspaceName = folder.provider_config.workspace_name
  const folderName = folder.provider_config.folder_name
  if (workspaceName && folderName) {
    return `${workspaceName} / ${folderName}`
  }
  return folderName || 'Folder'
}

function sourcePath(folder: SyncFolder): string {
  if (folder.provider_type === 'imanage') {
    return imanageSourcePath(folder)
  }
  return folder.provider_config.folder_path || folder.provider_config.folder_name || 'Unnamed Folder'
}

const IMANAGE_REAUTH_REQUIRED_REASON = 'auth_reauth_required'
const IMANAGE_INVALID_SOURCE_REASON = 'invalid_source'
const IMANAGE_INVALID_SOURCE_REASON_V1_LEGACY = 'config_invalid_root_folder'
const IMANAGE_REPEATED_FAILURES_REASON = 'repeated_failures'
const SYNC_TOO_LARGE_REASON = 'sync_too_large'

function isIManageWorkspaceSync(folder: SyncFolder): boolean {
  return (folder.provider_config.folder_id?.split(':') || []).length === 2
}

function imanageLibraryId(folder: SyncFolder): string | undefined {
  return folder.provider_config.library_id || folder.provider_config.database
}

function stripLibraryPrefix(value?: string): string | undefined {
  if (!value) return undefined
  return value.includes('!') ? value.split('!')[1] : value
}

function imanageWorkspaceHref(folder: SyncFolder): string | null {
  const libraryId = imanageLibraryId(folder)
  const workspaceId = stripLibraryPrefix(folder.provider_config.workspace_id)
    ?? stripLibraryPrefix(folder.provider_config.folder_id?.split(':')[1])
  if (!libraryId || !workspaceId) return null
  return `https://cloudimanage.com/work/web/r/libraries/${libraryId}/workspaces/${libraryId}!${workspaceId}`
}

function imanageFolderHref(folder: SyncFolder): string | null {
  const libraryId = imanageLibraryId(folder)
  const folderIdOnly = folder.provider_config.folder_id?.split(':')[2]
  if (!libraryId || !folderIdOnly) return null
  return `https://cloudimanage.com/work/web/r/libraries/${libraryId}/folders/${libraryId}!${folderIdOnly}`
}

function imanageSourceName(folder: SyncFolder): string {
  if (isIManageWorkspaceSync(folder)) {
    return folder.provider_config.workspace_name || folder.provider_config.folder_name || 'Unnamed Workspace'
  }
  return folder.provider_config.folder_name || 'Unnamed Folder'
}

function imanageKindLabel(members: SyncFolder[]): string | null {
  if (members[0]?.provider_type !== 'imanage' || !members[0].provider_config.folder_id) {
    return null
  }
  const workspaceCount = members.filter(isIManageWorkspaceSync).length
  const folderCount = members.length - workspaceCount
  if (members.length === 1) {
    return workspaceCount === 1 ? 'Workspace' : 'Folder'
  }
  if (folderCount === 0) return 'Workspaces'
  if (workspaceCount === 0) return 'Folders'
  return 'Workspaces and folders'
}

function groupedHeadingCountLabel(members: SyncFolder[]): string {
  const workspaceCount = members.filter(isIManageWorkspaceSync).length
  const folderCount = members.length - workspaceCount
  if (folderCount === 0) {
    return `${members.length} workspaces`
  }
  if (workspaceCount === 0) {
    return `${members.length} folders`
  }
  const workspaceLabel = workspaceCount === 1 ? 'workspace' : 'workspaces'
  const folderLabel = folderCount === 1 ? 'folder' : 'folders'
  return `${workspaceCount} ${workspaceLabel}, ${folderCount} ${folderLabel}`
}

function sourceDisplayName(folder: SyncFolder): string {
  if (folder.provider_type === 'imanage') {
    return imanageSourceName(folder)
  }
  return folder.provider_config.folder_name || 'Unnamed Folder'
}

function sortSyncFoldersByName(folders: SyncFolder[]): SyncFolder[] {
  return [...folders].sort((left, right) => (
    sourceDisplayName(left).localeCompare(sourceDisplayName(right), undefined, {
      numeric: true,
      sensitivity: 'base',
    })
  ))
}

function ProviderSectionIcon({ title }: { title: string }) {
  if (title === 'Outlook') {
    return <Mail className="h-4 w-4 text-gray-500" />
  }
  if (title === 'OneDrive') {
    return <MicrosoftProviderIcon provider="onedrive" className="h-4 w-4" />
  }
  if (title === 'SharePoint') {
    return <MicrosoftProviderIcon provider="sharepoint" className="h-4 w-4" />
  }
  return (
    <img src="/iManage-logo.png" alt="" className="h-4 w-4 object-contain opacity-70" />
  )
}

function SyncLinkTooltip({
  path,
  lastSyncedAt,
  children,
}: {
  path: string
  lastSyncedAt: string | null
  children: ReactNode
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent className="max-w-none">
        <p className="whitespace-nowrap">{path}</p>
        <p className="mt-1 text-xs text-stone-500">Last synced {formatLastSync(lastSyncedAt)}</p>
      </TooltipContent>
    </Tooltip>
  )
}

function sourceHref(folder: SyncFolder): string | null {
  const canLink = folder.provider_type === 'imanage'
    && Boolean(folder.provider_config.database || folder.provider_config.library_id)
    && Boolean(folder.provider_config.folder_id)
  if (!canLink) return null
  return isIManageWorkspaceSync(folder) ? imanageWorkspaceHref(folder) : imanageFolderHref(folder)
}

function SourceLabel({ folder }: { folder: SyncFolder }) {
  const name = sourceDisplayName(folder)
  const href = sourceHref(folder)
  const className = href
    ? 'text-blue-600 hover:text-blue-800 hover:underline'
    : undefined
  return (
    <SyncLinkTooltip path={sourcePath(folder)} lastSyncedAt={folder.last_synced_at}>
      {href ? (
        <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
          {name}
        </a>
      ) : (
        <span className={className}>{name}</span>
      )}
    </SyncLinkTooltip>
  )
}

function SourceChip({ folder }: { folder: SyncFolder }) {
  const name = sourceDisplayName(folder)
  const href = sourceHref(folder)
  const chipClassName = 'inline-flex max-w-[11rem] items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700 hover:bg-slate-200'
  const label = <span className="truncate">{name}</span>
  return (
    <SyncLinkTooltip path={sourcePath(folder)} lastSyncedAt={folder.last_synced_at}>
      {href ? (
        <a href={href} target="_blank" rel="noopener noreferrer" className={chipClassName}>
          {label}
        </a>
      ) : (
        <span className={chipClassName}>{label}</span>
      )}
    </SyncLinkTooltip>
  )
}

function DestinationLabel({
  folder,
  matterGuid,
  lastSyncedAt,
}: {
  folder: SyncFolder
  matterGuid: string
  lastSyncedAt: string | null
}) {
  const name = folder.destination_folder_name || 'Root'
  const className = 'text-blue-600 hover:text-blue-800 hover:underline font-medium'
  const link = folder.destination_folder_path?.toLowerCase() === CORRESPONDENCE_FOLDER_PATH ? (
    <Link to="/matter/$slug/case-files/correspondence" params={{ slug: matterGuid }} className={className}>
      {name}
    </Link>
  ) : folder.destination_folder_guid ? (
    <Link
      to="/matter/$slug/case-files/folder/$folderGuid"
      params={{ slug: matterGuid, folderGuid: folder.destination_folder_guid }}
      className={className}
    >
      {name}
    </Link>
  ) : (
    <Link to="/matter/$slug/case-files/all-files" params={{ slug: matterGuid }} className={className}>
      {name}
    </Link>
  )
  return (
    <SyncLinkTooltip path={crimsonDestinationPath(folder)} lastSyncedAt={lastSyncedAt}>
      {link}
    </SyncLinkTooltip>
  )
}

export function SyncManagement({ matterGuid, className }: SyncManagementProps) {
  const label = useMatterLabels()
  const [syncFolders, setSyncFolders] = useState<SyncFolder[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [deletionInfo, setDeletionInfo] = useState<DeletionInfo | null>(null)
  const [deletionMode, setDeletionMode] = useState<'delete_files' | 'keep_files'>('keep_files')
  const [isDeleting, setIsDeleting] = useState(false)
  const [selectedSyncIds, setSelectedSyncIds] = useState<number[]>([])
  const [currentUserId, setCurrentUserId] = useState<number | null>(null)
  const [revalidatingSyncId, setRevalidatingSyncId] = useState<number | null>(null)
  const { runOAuthPopup } = useOAuthPopupFlow()
  const { isEnabled: iManageEnabled } = useFeatures(FEATURE_FLAGS.IMANAGE)
  const { isEnabled: oneDriveEnabled } = useFeatures(FEATURE_FLAGS.ONEDRIVE)
  const { isEnabled: sharePointEnabled } = useFeatures(FEATURE_FLAGS.SHAREPOINT)
  const availableProviders = getAvailableSyncProviders({
    iManageEnabled,
    oneDriveEnabled,
    sharePointEnabled
  })

  useEffect(() => {
    loadSyncFolders()
  }, [matterGuid])

  const loadSyncFolders = async () => {
    setIsLoading(true)
    try {
      const response = await backendClient.get(`/sync/folders?matter_guid=${matterGuid}`)
      const loadedSyncFolders = response.data.sync_folders || []
      setSyncFolders(loadedSyncFolders)
      setCurrentUserId(response.data.current_user_id || null)
    } catch (err) {
      console.error('Failed to load sync folders:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSyncAdded = async () => {
    await loadSyncFolders()
  }

  const handleOpenDeleteDialog = async (folder: SyncFolder) => {
    setSelectedSyncIds([folder.id])
    setDeletionMode('keep_files') // Default to safer option

    try {
      const response = await backendClient.get(`/sync/folders/${folder.id}/deletion-info`, {
        params: folder.group_id ? { group_id: folder.group_id } : undefined
      })
      setDeletionInfo(response.data)
    } catch (err) {
      console.error('Failed to load deletion info:', err)
      // Show error or fallback message
      setDeletionInfo(null)
    }
  }

  const handleConfirmDelete = async () => {
    if (selectedSyncIds.length === 0) return

    const selectedFolder = syncFolders.find((folder) => folder.id === selectedSyncIds[0])
    setIsDeleting(true)
    try {
      await backendClient.delete(`/sync/folders/${selectedSyncIds[0]}`, {
        data: {
          deletion_mode: deletionMode,
          group_id: selectedFolder?.group_id || undefined
        }
      })
      await loadSyncFolders()
      setSelectedSyncIds([])
      setDeletionInfo(null)
    } catch (err) {
      console.error('Failed to remove sync:', err)
      const detail =
        typeof err === 'object' && err !== null && 'response' in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : undefined
      toast.error(detail || 'Failed to remove sync')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleReauthenticate = async (syncFolder: SyncFolder): Promise<number | null> => {
    // For iManage, trigger the OAuth flow again
    if (syncFolder.provider_type === 'imanage') {
      try {
        // Initiate OAuth flow
        const response = await backendClient.post('/sync/auth/initiate', {
          provider_type: 'imanage',
          matter_guid: matterGuid,
          destination_folder_id: syncFolder.destination_folder_id,
          // Include existing workspace/folder info to reconnect the same sync
          workspace_id: syncFolder.provider_config.workspace_id,
          folder_id: syncFolder.provider_config.folder_id,
          sync_folder_id: syncFolder.id  // Pass sync ID to update existing sync
        })

        const data = response.data
        if (!data?.auth_url || !data?.state) {
          toast.error('Unable to start iManage re-authentication. Please try again.')
          return null
        }

        await runOAuthPopup({
          authUrl: data.auth_url,
          popupName: 'IManageReconnect',
          successMessageType: 'imanage-auth-success',
          acknowledgmentMessageType: 'imanage-auth-acknowledged',
          stateStorage: {
            key: 'imanage_oauth_state',
            value: data.state
          },
          extractAuthData: (eventData) => eventData?.authData || null
        })

        await loadSyncFolders()
        toast.success('iManage account reconnected')
      } catch (err) {
        console.error('Failed to initiate re-authentication:', err)
        if (err instanceof Error && err.message === 'Authentication cancelled: popup was closed') {
          return null
        }
        const detail =
          typeof err === 'object' && err !== null && 'response' in err
            ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
            : undefined
        toast.error(detail || 'Failed to initiate iManage re-authentication')
      }
      return null
    }

    if (syncFolder.provider_type === 'onedrive') {
      const sourceProvider =
        isMicrosoftFilesProvider(syncFolder.provider_config?.source_provider)
          ? syncFolder.provider_config.source_provider
          : null
      const sourceLabel = getMicrosoftFilesSourceLabel(sourceProvider)
      try {
        const response = await backendClient.get('/onedrive/oauth/authorize', {
          params: { source_provider: sourceProvider || 'onedrive' }
        })
        const data = response.data
        if (!data?.auth_url) {
          toast.error(`Unable to reconnect ${sourceLabel} right now. Please try again.`)
          return null
        }

        const authUrl = new URL(data.auth_url)
        const oauthState = authUrl.searchParams.get('state')
        if (!oauthState) {
          toast.error(`Invalid ${sourceLabel} authorization URL. Please try again.`)
          return null
        }
        if (sourceProvider) {
          sessionStorage.setItem(MICROSOFT_FILES_OAUTH_PROVIDER_KEY, sourceProvider)
        } else {
          sessionStorage.removeItem(MICROSOFT_FILES_OAUTH_PROVIDER_KEY)
        }
        const authData = await runOAuthPopup<{ access_token: string; oauth_account_id: number | string }>({
          authUrl: data.auth_url,
          popupName: 'OneDriveReconnect',
          successMessageType: 'onedrive-auth-success',
          acknowledgmentMessageType: 'onedrive-auth-acknowledged',
          stateStorage: {
            key: 'onedrive_oauth_state',
            value: oauthState
          },
          extractAuthData: (eventData) => {
            if (!eventData?.authData?.access_token || !eventData?.authData?.oauth_account_id) {
              return null
            }
            return eventData.authData
          }
        }).finally(() => {
          sessionStorage.removeItem(MICROSOFT_FILES_OAUTH_PROVIDER_KEY)
        })

        await loadSyncFolders()
        toast.success(`${sourceLabel} account reconnected`)

        const newAccountId = Number(authData.oauth_account_id)
        return Number.isFinite(newAccountId) ? newAccountId : null
      } catch (err) {
        console.error(`Failed to reconnect ${sourceLabel}:`, err)
        if (err instanceof Error && err.message === 'Authentication cancelled: popup was closed') {
          return null
        }
        const detail =
          typeof err === 'object' && err !== null && 'response' in err
            ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
            : undefined
        toast.error(detail || `Failed to reconnect ${sourceLabel}`)
      }
    }
    return null
  }

  const handleRevalidate = async (syncFolder: SyncFolder, { allowReconnect = true }: { allowReconnect?: boolean } = {}) => {
    const sourceLabel = getMicrosoftFilesSourceLabel(
      syncFolder.provider_config?.source_provider ?? 'onedrive'
    )
    setRevalidatingSyncId(syncFolder.id)
    try {
      const response = await backendClient.post(
        `/sync/folders/${syncFolder.id}/revalidate?matter_guid=${matterGuid}`
      )
      const status = response.data?.status
      if (status === 'recovered') {
        const queued = response.data?.queued === true
        toast.success(
          queued
            ? `${sourceLabel} folder found. Sync re-enabled and syncing now.`
            : `${sourceLabel} folder found. Sync re-enabled; a sync will start shortly.`
        )
        await loadSyncFolders()
      } else if (status === 'still_missing') {
        toast.error(`The ${sourceLabel} folder still can't be found. Remove this sync or point it at a different folder.`)
        await loadSyncFolders()
      } else if (status === 'needs_reconnect') {
        toast.error(`${sourceLabel} needs to be reconnected before this sync can recover.`)
        if (!allowReconnect) {
          await loadSyncFolders()
          return
        }
        // Reconnecting mints a new OAuth account; the sync's oauth_account_id may
        // have been nulled when the old account was removed, so relink it before
        // retrying recovery. Retry once with reconnect disabled to avoid loops.
        const newAccountId = await handleReauthenticate(syncFolder)
        if (newAccountId != null) {
          await backendClient.post(
            `/sync/folders/${syncFolder.id}/relink-account?matter_guid=${matterGuid}`,
            { oauth_account_id: newAccountId }
          )
          await handleRevalidate(syncFolder, { allowReconnect: false })
        }
      } else {
        await loadSyncFolders()
      }
    } catch (err) {
      console.error('Failed to re-check sync folder:', err)
      const detail =
        typeof err === 'object' && err !== null && 'response' in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : undefined
      toast.error(detail || 'Failed to re-check folder')
    } finally {
      setRevalidatingSyncId(null)
    }
  }

  const syncWarning = (folder: SyncFolder) => {
    const oneDriveAccountStatus = folder.provider_type === 'onedrive'
      ? folder.provider_config?.oauth_account_status
      : null
    const oneDriveNeedsReconnect = folder.provider_type === 'onedrive'
      && oneDriveAccountStatus
      && oneDriveAccountStatus !== 'active'
    const v1DisabledReason = folder.sync_state?.disabled_reason ?? null
    const effectiveDisabledReason = v1DisabledReason
      ?? folder.sync_state?.v2?.disabled_reason
      ?? null
    const effectiveLastSyncError = folder.sync_state?.last_sync_error
      ?? folder.sync_state?.v2?.last_sync_error
      ?? null
    const sourceMissing = folder.provider_type === 'onedrive'
      && effectiveDisabledReason === 'remote_item_not_found'
    const iManageNeedsReconnect = folder.provider_type === 'imanage'
      && effectiveDisabledReason === IMANAGE_REAUTH_REQUIRED_REASON
    const iManageInvalidFolder = folder.provider_type === 'imanage'
      && (effectiveDisabledReason === IMANAGE_INVALID_SOURCE_REASON
        || v1DisabledReason === IMANAGE_INVALID_SOURCE_REASON_V1_LEGACY)
    const iManageRepeatedFailures = folder.provider_type === 'imanage'
      && effectiveDisabledReason === IMANAGE_REPEATED_FAILURES_REASON
    const syncTooLarge = effectiveDisabledReason === SYNC_TOO_LARGE_REASON
    const isInactiveSync = !folder.is_active
    const showWarning = oneDriveNeedsReconnect || sourceMissing || iManageNeedsReconnect || iManageInvalidFolder || iManageRepeatedFailures || syncTooLarge || isInactiveSync
    const microsoftSourceLabel = getMicrosoftFilesSourceLabel(folder.provider_config?.source_provider ?? 'onedrive')
    const warningTitle = oneDriveNeedsReconnect
      ? `Sync Blocked - ${microsoftSourceLabel} Reconnection Required`
      : iManageNeedsReconnect
        ? 'Sync Blocked - iManage Reconnection Required'
        : iManageInvalidFolder
          ? 'Sync Blocked - Invalid Source'
          : iManageRepeatedFailures
            ? 'Sync Paused - Repeated Failures'
            : syncTooLarge
              ? 'Sync File Count Exceeds Default Limit'
              : sourceMissing
                ? `${microsoftSourceLabel} folder no longer exists`
                : 'Sync is Inactive'
    const warningBody = oneDriveNeedsReconnect
      ? 'This account needs reconnection before sync jobs can run.'
      : iManageNeedsReconnect
        ? 'iManage needs reauthentication before this sync can run. Reconnect iManage or delete this sync if it is no longer needed.'
        : iManageInvalidFolder
          ? (effectiveLastSyncError?.message
            || 'This sync\'s iManage source is not a valid folder. It may have been deleted, or the selected item is not a folder. Remove this sync or set up a new one pointing at a folder.')
          : iManageRepeatedFailures
            ? (effectiveLastSyncError?.message
              || 'This sync failed repeatedly and has been paused. Check iManage setup.')
            : syncTooLarge
              ? 'By default, Crimson supports syncing up to 10,000 files per sync. If your use case requires higher limits, please contact support@crimson.law.'
              : sourceMissing
                ? `The ${microsoftSourceLabel} folder this sync was watching has been deleted or is no longer accessible. Remove this sync or set up a new one pointing at a different folder.`
                : 'This sync configuration is inactive and will not run automatically.'
    return {
      showWarning,
      warningTitle,
      warningBody,
      showReconnectButton: oneDriveNeedsReconnect || iManageNeedsReconnect,
      sourceMissing,
      warningButtonText: folder.provider_type === 'onedrive'
        ? `Reconnect ${microsoftSourceLabel}`
        : 'Reconnect iManage',
    }
  }

  // Helper function to render sync folder cards
  const renderSyncFolders = (folders: SyncFolder[]) => {
    return (
      <TooltipProvider delayDuration={200}>
      <div className="space-y-4">
        {clusterSyncFolders(folders).map((cluster) => {
          const members = sortSyncFoldersByName(cluster)
          const folder = members[0]
          const createdBy = folder.created_by_user_name || folder.created_by_user_email
          const kindLabel = imanageKindLabel(members)
          const freshness = groupedFreshness(members, (item) => syncWarning(item).showWarning)
          const folderWorkspaces = members.filter((item) => !isIManageWorkspaceSync(item) && item.provider_config.workspace_name)
          const uniqueWorkspaces = folderWorkspaces.filter((item, index) => (
            folderWorkspaces.findIndex((other) => (
              other.provider_config.workspace_id === item.provider_config.workspace_id
              && other.provider_config.workspace_name === item.provider_config.workspace_name
            )) === index
          ))
          const uniqueLibraries = members
            .map(imanageLibraryId)
            .filter((value, index, all): value is string => Boolean(value) && all.indexOf(value) === index)
          const warnings = members
            .map((item) => ({ item, warning: syncWarning(item) }))
            .filter((entry) => entry.warning.showWarning)
          const reconnectWarning = warnings.find(({ warning }) => warning.showReconnectButton)
          return (
            <div
              key={folder.group_id || folder.id}
              className="rounded-lg border border-border/70 p-4 transition-colors hover:border-slate-300 hover:bg-slate-50"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  {kindLabel && (
                    <div className="mb-1 text-xs text-gray-500">
                      {kindLabel}
                    </div>
                  )}
                  {members.length > 1 ? (
                    <>
                      <div className="mb-2 flex min-w-0 flex-wrap items-center gap-1.5 text-sm font-semibold text-gray-900">
                        <span>{groupedHeadingCountLabel(members)}</span>
                        <ArrowRight className="shrink-0 text-gray-400" size={16} />
                        <DestinationLabel
                          folder={folder}
                          matterGuid={matterGuid}
                          lastSyncedAt={freshness.at}
                        />
                      </div>
                      <div className="mb-4 flex flex-wrap gap-1.5">
                        {members.map((item) => (
                          <SourceChip key={item.id} folder={item} />
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="mb-4 flex w-fit max-w-full items-center gap-2 text-sm font-medium text-gray-900">
                      <SourceLabel folder={folder} />
                      <ArrowRight className="shrink-0 text-gray-400" size={16} />
                      <DestinationLabel
                        folder={folder}
                        matterGuid={matterGuid}
                        lastSyncedAt={folder.last_synced_at}
                      />
                    </div>
                  )}

                  <div className="space-y-0.5 text-xs">
                    {folder.provider_type === 'outlook' && folder.provider_config.user_email && (
                      <div className="flex items-center gap-1.5 text-gray-500">
                        <Mail className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                        <span>Mailbox</span>
                        <span className="font-medium text-gray-900">{folder.provider_config.user_email}</span>
                      </div>
                    )}
                    {folder.provider_type === 'onedrive' && folder.provider_config.user_email && (
                      <div className="flex items-center gap-1.5 text-gray-500">
                        <UserRound className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                        <span>Account</span>
                        <span className="font-medium text-gray-900">{folder.provider_config.user_email}</span>
                      </div>
                    )}
                    {folder.provider_type === 'imanage' && (
                      <div className="space-y-0.5">
                        {uniqueWorkspaces.length > 0 && (
                          <div className="flex items-start gap-1.5 text-gray-500">
                            <Building2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" />
                            <span>{uniqueWorkspaces.length === 1 ? 'Workspace' : 'Workspaces'}</span>
                            <span className="min-w-0 font-medium text-gray-900">
                              {uniqueWorkspaces.map((item, index) => {
                                const workspaceHref = imanageWorkspaceHref(item)
                                const workspaceName = item.provider_config.workspace_name
                                return (
                                  <Fragment key={`${item.id}-workspace`}>
                                    {index > 0 ? ', ' : ''}
                                    {workspaceHref ? (
                                      <a
                                        href={workspaceHref}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:text-blue-800 hover:underline"
                                      >
                                        {workspaceName}
                                      </a>
                                    ) : (
                                      workspaceName
                                    )}
                                  </Fragment>
                                )
                              })}
                            </span>
                          </div>
                        )}
                        {uniqueLibraries.map((libraryId) => (
                          <div key={libraryId} className="flex items-center gap-1.5 text-gray-500">
                            <Database className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                            <span>Library</span>
                            <span className="font-medium text-gray-900">{libraryId}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="mt-2 space-y-0.5 text-xs">
                    {createdBy && (
                      <div className="flex items-center gap-1.5 text-gray-500">
                        <UserRound className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                        <span>Created by</span>
                        <span className="font-medium text-gray-900">{createdBy}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1.5 text-gray-500">
                      {freshness.status === 'updating' ? (
                        <>
                          <RefreshCw className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                          <span className="font-medium text-gray-900">Updating Crimson</span>
                        </>
                      ) : (
                        <>
                          <Clock className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                          <span>Last synced</span>
                          <span className="font-medium text-gray-900">
                            {freshness.status === 'pending'
                              ? 'Pending first sync (may take a few minutes)'
                              : formatLastSync(freshness.at)}
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  {warnings.map(({ item, warning }) => (
                    <div key={`${item.id}-warning`} className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-red-900 mb-1">
                            {members.length > 1 ? `${imanageSourceName(item)} — ${warning.warningTitle}` : warning.warningTitle}
                          </p>
                          <p className="text-xs text-red-700">
                            {warning.warningBody}
                          </p>
                          {warning.sourceMissing && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRevalidate(item)}
                              disabled={revalidatingSyncId === item.id}
                              className="mt-2 text-xs h-7 border-red-300 text-red-700 hover:bg-red-100 hover:text-red-900"
                            >
                              {revalidatingSyncId === item.id ? (
                                <>
                                  <Spinner size="xs" className="mr-1" />
                                  Checking…
                                </>
                              ) : (
                                <>
                                  <RefreshCw className="h-3 w-3 mr-1" />
                                  Re-check folder
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex shrink-0 flex-col gap-2">
                  <AlertDialog open={selectedSyncIds[0] === folder.id} onOpenChange={(open) => {
                    if (!open) {
                      setSelectedSyncIds([])
                      setDeletionInfo(null)
                    }
                  }}>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        icon={Trash2}
                        text="Delete"
                        onClick={() => handleOpenDeleteDialog(folder)}
                        disabled={currentUserId !== folder.created_by_user_id}
                        title={currentUserId !== folder.created_by_user_id ? 'Only the creator can delete this sync' : 'Delete sync'}
                        className="w-full"
                      />
                    </AlertDialogTrigger>
                    <AlertDialogContent className="max-w-md">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                          <AlertTriangle className="h-5 w-5 text-amber-500" />
                          Remove Sync Configuration
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-left space-y-4 pt-2">
                          {deletionInfo ? (
                            <>
                              {deletionInfo.provider_type === 'outlook' ? (
                                <p className="text-sm text-gray-700">
                                  Synced emails will stay in Crimson but automatic syncing from this folder will stop.
                                </p>
                              ) : (
                                <>
                                  <p className="text-sm text-gray-700">
                                    {(deletionInfo.sync_folder_ids?.length ?? 1) > 1 ? (
                                      <>
                                        These {deletionInfo.folder_name} contain{' '}
                                        <span className="font-semibold">
                                          {deletionInfo.file_count} file{deletionInfo.file_count !== 1 ? 's' : ''}
                                        </span>
                                        .
                                      </>
                                    ) : (
                                      <>
                                        This sync contains{' '}
                                        <span className="font-semibold">
                                          {deletionInfo.file_count} file{deletionInfo.file_count !== 1 ? 's' : ''}
                                        </span>
                                        .
                                      </>
                                    )}
                                    {' '}What would you like to do with {deletionInfo.file_count === 1 ? 'it' : 'them'}?
                                  </p>

                                  <div className="space-y-2">
                                    <button
                                      type="button"
                                      onClick={() => setDeletionMode('keep_files')}
                                      className={`w-full flex items-center gap-3 rounded-md px-4 py-3 text-left transition-all border ${
                                        deletionMode === 'keep_files'
                                          ? 'border-gray-900 bg-gray-50'
                                          : 'border-gray-200 hover:border-gray-300 bg-white'
                                      }`}
                                    >
                                      <div className={`shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                        deletionMode === 'keep_files'
                                          ? 'border-gray-900 bg-gray-900'
                                          : 'border-gray-300'
                                      }`}>
                                        {deletionMode === 'keep_files' && (
                                          <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
                                        )}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium text-gray-900">
                                          Keep files in Crimson
                                        </div>
                                        <p className="text-xs text-gray-600 mt-0.5">
                                          Files remain accessible, sync stops
                                        </p>
                                      </div>
                                    </button>

                                    <button
                                      type="button"
                                      onClick={() => setDeletionMode('delete_files')}
                                      className={`w-full flex items-center gap-3 rounded-md px-4 py-3 text-left transition-all border ${
                                        deletionMode === 'delete_files'
                                          ? 'border-red-600 bg-red-50'
                                          : 'border-gray-200 hover:border-gray-300 bg-white'
                                      }`}
                                    >
                                      <div className={`shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                        deletionMode === 'delete_files'
                                          ? 'border-red-600 bg-red-600'
                                          : 'border-gray-300'
                                      }`}>
                                        {deletionMode === 'delete_files' && (
                                          <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
                                        )}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium text-gray-900">
                                          Delete all files
                                        </div>
                                        <p className="text-xs text-gray-600 mt-0.5">
                                          Permanently removes files from Crimson
                                        </p>
                                      </div>
                                    </button>
                                  </div>
                                </>
                              )}
                            </>
                          ) : (
                            <div className="flex items-center justify-center py-4">
                              <Spinner size="md" />
                            </div>
                          )}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={async (event) => {
                            event.preventDefault()
                            await handleConfirmDelete()
                          }}
                          disabled={!deletionInfo || isDeleting}
                          className={deletionInfo?.provider_type === 'outlook' ? 'bg-primary hover:bg-primary/90' : (deletionMode === 'delete_files' ? 'bg-red-600 hover:bg-red-700' : 'bg-primary hover:bg-primary/90')}
                        >
                          {isDeleting ? (
                            <>
                              <Spinner size="sm" className="mr-2" />
                              Removing...
                            </>
                          ) : (
                            deletionInfo?.provider_type === 'outlook' ? 'Continue' : 'Confirm'
                          )}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  {reconnectWarning && (
                    <Button
                      variant="outline"
                      size="sm"
                      icon={RefreshCw}
                      text={reconnectWarning.warning.warningButtonText}
                      onClick={() => handleReauthenticate(reconnectWarning.item)}
                      className="w-full"
                    />
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
      </TooltipProvider>
    )
  }

  // Filter sync folders by provider type
  const outlookSyncs = syncFolders.filter(f => f.provider_type === 'outlook')
  const imanageSyncs = syncFolders.filter(f => f.provider_type === 'imanage')
  const oneDriveSyncs = syncFolders.filter(
    f => f.provider_type === 'onedrive' && f.provider_config?.source_provider !== 'sharepoint'
  )
  const sharePointSyncs = syncFolders.filter(
    f => f.provider_type === 'onedrive' && f.provider_config?.source_provider === 'sharepoint'
  )

  const syncGroups = [
    { title: 'Outlook', folders: outlookSyncs },
    { title: 'iManage', folders: imanageSyncs },
    { title: 'OneDrive', folders: oneDriveSyncs },
    { title: 'SharePoint', folders: sharePointSyncs }
  ].filter(group => group.folders.length > 0)

  if (isLoading) {
    return (
      <div className={`p-8 text-center ${className || ''}`}>
        <p className="text-sm text-gray-500">Loading sync configurations...</p>
      </div>
    )
  }

  return (
    <div className={className}>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <CardTitle>Folder Syncs</CardTitle>
              <CardDescription>
                {label('folder-sync-manage-hint')}
              </CardDescription>
            </div>
            <AddSyncDialog matterGuid={matterGuid} syncFolders={syncFolders} onSyncAdded={handleSyncAdded} />
          </div>
        </CardHeader>

        <CardContent>
          {syncFolders.length === 0 ? (
            <div className="grid gap-3">
              {availableProviders.map((provider) => (
                <AddSyncDialog
                  key={provider.type}
                  matterGuid={matterGuid}
                  syncFolders={syncFolders}
                  onSyncAdded={handleSyncAdded}
                  initialProvider={provider.type}
                  trigger={<SyncProviderCard provider={provider} />}
                />
              ))}
            </div>
          ) : (
            <div className="space-y-8">
              {syncGroups.map((group) => (
                <div key={group.title}>
                  <h3 className="mb-3 flex items-center gap-2 text-sm font-medium text-gray-900">
                    <ProviderSectionIcon title={group.title} />
                    {group.title}
                  </h3>
                  {renderSyncFolders(group.folders)}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
